import { useMemo, useState } from 'react';
import jwtDecode from 'jwt-decode';
import { ClickAwayListener, Grow, Hidden, MenuItem, MenuList, Paper, Popper, Typography } from '@mui/material';
import Button, { ButtonProps } from '@mui/material/Button';
import clsx from 'clsx';
import { useAuthUserStore, useImpersonatedUserStore, useLoggedOnBehalfStore, usePartnersStore } from '@lib/store';
import { AuthUtils, LocalStorageUtils } from '@shared/utils';
import { type JwtToken } from '@shared/api';
import { useStyles } from './styles';

interface Props extends Omit<ButtonProps, 'onClick'> {}

export const AccountButton = ({ ...rest }: Props) => {
  const { data: authUser } = useAuthUserStore();
  const { partners, selectedPartnerId, setSelectedPartnerId } = usePartnersStore();
  const { data: loggedOnBehalf } = useLoggedOnBehalfStore();
  const { data: impersonatedUser } = useImpersonatedUserStore();

  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const open = Boolean(anchorEl);

  const classes = useStyles();

  const handleClick = (event: MouseEvent | TouchEvent | React.MouseEvent<HTMLButtonElement>) => {
    const { currentTarget } = event;
    setAnchorEl(anchorEl || !(currentTarget instanceof HTMLElement) ? undefined : currentTarget);
  };

  const handleSelectPartner = (partnerId: number) => {
    setSelectedPartnerId(partnerId);
    LocalStorageUtils.setSelectedPartnerId(partnerId);
    setAnchorEl(undefined);
    window.location.reload();
  };

  const token: JwtToken | undefined = useMemo(() => {
    if (authUser?.accessToken) return jwtDecode(authUser.accessToken);
    else return undefined;
  }, [authUser?.accessToken]);

  return (
    <>
      {loggedOnBehalf && impersonatedUser && <Typography style={{ display: 'block' }}>Currently logged in as {impersonatedUser}</Typography>}
      <Button onClick={handleClick} {...rest}>
        <Hidden smDown>
          <span className={classes.btnText}>{token?.preferred_username || 'Account'}</span>
        </Hidden>
      </Button>

      <Popper
        className={clsx(classes.popperResponsive, { [classes.popperClose]: !open })}
        placement='bottom-start'
        open={open}
        anchorEl={anchorEl}
        transition
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClick}>
            <Grow {...TransitionProps}>
              <Paper className={classes.dropdown}>
                <MenuList role='menu'>
                  {partners &&
                    partners.length > 1 &&
                    partners.map((partner, idx) => (
                      <MenuItem
                        key={partner.id}
                        className={classes.dropdownItem}
                        selected={selectedPartnerId === partner.id}
                        divider={idx === partners.length - 1}
                        onClick={() => handleSelectPartner(partner.id)}
                      >
                        {partner.name}
                      </MenuItem>
                    ))}
                  {token?.client_HomePage && (
                    <MenuItem className={classes.dropdownItem} onClick={() => (document.location.href = token.client_HomePage || '')}>
                      Home
                    </MenuItem>
                  )}
                  <MenuItem className={classes.dropdownItem} onClick={AuthUtils.logOut}>
                    Log out
                  </MenuItem>
                </MenuList>
              </Paper>
            </Grow>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};
